import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { AddCircleOutline, Visibility } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import LeaveViewController from "../../redux/actions/leaveApiAction";
import LeaveRequestFormModal from "./AddLeaveRequest";
import ViewLeaveModal from "./ViewLeaveInfo";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx, inputSx } from "../../components/auth/authTheme";
import { getUserInfoFromToken } from "../../utils/authUtils";
import usePermission from "../../utils/usePermission";
import LeaveMobileView from "./LeaveMobileView";

import "../../styles/styles.css";
import "../../styles/layout.css";

const LeaveStatusBadge = ({ status }) => (
  <span className={`leave-status-badge ${status || "pending"}`}>
    {status || "pending"}
  </span>
);

const LeaveListPage = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hasPermission } = usePermission();

  const [userType, setUserType] = useState(null);
  const { getLeaves, updateLeaveStatus } = LeaveViewController();
  const { leaves } = useSelector((state) => state.leave);

  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isEmployee = (userType || "").toLowerCase() === "employee";
  const canCreate = hasPermission("leave_list", "create");
  const canExport = hasPermission("leave_list", "export");
  const canApprove =
    hasPermission("leave_list", "approve") ||
    hasPermission("leave_list", "reject");

  useEffect(() => {
    const { userType: role } = getUserInfoFromToken();
    setUserType(role);
  }, []);

  useEffect(() => {
    dispatch(getLeaves());
  }, [dispatch]);

  const openModalForCreate = () => {
    setShowModal(true);
  };

  const handleView = useCallback((leave) => {
    setSelectedLeave(leave);
    setShowViewModal(true);
  }, []);

  const handleStatusChange = useCallback(
    (leaveId, selectedStatus) => {
      dispatch(updateLeaveStatus(leaveId, selectedStatus));
    },
    [dispatch, updateLeaveStatus]
  );

  const filteredLeaves = useMemo(() => {
    if (!searchTerm.trim()) return leaves || [];
    const query = searchTerm.toLowerCase();
    return (leaves || []).filter(
      (leave) =>
        leave.employee?.toLowerCase().includes(query) ||
        leave.leave_type?.toLowerCase().includes(query) ||
        leave.approval_status?.toLowerCase().includes(query)
    );
  }, [leaves, searchTerm]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      ...(!isEmployee
        ? [
            {
              headerName: "Employee",
              field: "employee",
              flex: 1,
              minWidth: 140,
            },
          ]
        : []),
      {
        headerName: "Leave Type",
        field: "leave_type",
        flex: 1,
        minWidth: 110,
        valueFormatter: (params) =>
          params.value
            ? params.value.charAt(0).toUpperCase() + params.value.slice(1)
            : "",
      },
      {
        headerName: "Leave Dates",
        valueGetter: (params) =>
          `${params.data.leave_start_date} – ${params.data.leave_end_date}`,
        flex: 1.2,
        minWidth: 180,
      },
      {
        headerName: "Total Days",
        field: "total_leave_days",
        width: 110,
        maxWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: "Status",
        field: "approval_status",
        width: 140,
        minWidth: 130,
        maxWidth: 160,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          const status = params.value || "pending";
          const leaveId = params.data.id;

          if (canApprove) {
            return (
              <select
                className={`leave-status-select ${status}`}
                value={status}
                onChange={(e) => handleStatusChange(leaveId, e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            );
          }

          return <LeaveStatusBadge status={status} />;
        },
      },
      {
        headerName: "Actions",
        pinned: "right",
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => handleView(params.data)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [isEmployee, canApprove, handleStatusChange, handleView]
  );

  return (
    <>
      {isMobile ? (
        <LeaveMobileView
          leaves={filteredLeaves}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleView={handleView}
          handleStatusChange={handleStatusChange}
          openModalForCreate={openModalForCreate}
          canCreate={canCreate}
          canApprove={canApprove}
          isEmployee={isEmployee}
        />
      ) : (
        <ListPageLayout
          title={isEmployee ? "My Leaves" : "Leave Requests"}
          subtitle={
            isEmployee
              ? "Apply for leave and track your request status"
              : "Review and manage employee leave requests"
          }
          headerAction={
            canCreate ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutline />}
                onClick={openModalForCreate}
                sx={toolbarButtonSx}
              >
                {isEmployee ? "Request Leave" : "Add Leave"}
              </Button>
            ) : null
          }
          toolbarRight={
            <>
              {canExport && (
                <ExportButton
                  gridRef={gridRef}
                  fileName="leaves.xlsx"
                  exportColumns={[
                    "employee",
                    "leave_type",
                    "leave_start_date",
                    "leave_end_date",
                    "total_leave_days",
                    "approval_status",
                  ]}
                  disabled={selectedRows.length === 0}
                />
              )}
              <TextField
                size="small"
                placeholder="Search leaves..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ ...inputSx, minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              rowData={filteredLeaves}
              columnDefs={columnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              onSelectionChanged={(rows) => setSelectedRows(rows)}
              emptyMessage="No leave requests yet"
              emptyHint={
                canCreate
                  ? 'Click "Request Leave" to submit your first leave application.'
                  : "No leave records are available."
              }
            />
          </div>
        </ListPageLayout>
      )}

      {showModal && (
        <LeaveRequestFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
        />
      )}

      {showViewModal && (
        <ViewLeaveModal
          show={showViewModal}
          handleClose={() => setShowViewModal(false)}
          selectedLeave={selectedLeave}
          isEmployee={isEmployee}
        />
      )}
    </>
  );
};

export default LeaveListPage;
